import { ReactElement, useEffect, useId, useMemo, useState } from 'react';

export type KycDocumentVerificationStatus = 'idle' | 'checking' | 'valid' | 'invalid' | 'rejected' | 'max_attempts';

export interface KycDocumentUploadPanelProps {
	disabled: boolean;
	previewUrl: string;
	previewKind?: 'image' | 'pdf';
	isUploading: boolean;
	verificationStatus: KycDocumentVerificationStatus;
	verificationMessage: string;
	onSelectFile: (file: File) => void;
	onTakePhotoClick: () => void;
	/** When true, do not show the green "document valid" line (e.g. auto-advance to selfie). */
	suppressValidMessage?: boolean;
	/** Clears bootstrap rejection and returns to empty upload state. */
	onTryAgain?: () => void;
}

const PersonPlusIcon = (): ReactElement => (
	<span className="kyc-doc-upload__person-icon" aria-hidden>
		<svg viewBox="0 0 24 24" width="14" height="14" focusable="false">
			<circle cx="12" cy="9" r="4" fill="currentColor" opacity={0.35} />
			<path
				d="M6 20c0-3.3 2.7-6 6-6s6 2.7 6 6"
				fill="none"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
			/>
			<circle cx="18" cy="6" r="2.5" fill="currentColor" />
			<path d="M18 4.2v3.6M16.2 6h3.6" stroke="currentColor" strokeWidth="1.2" />
		</svg>
	</span>
);

const KycDocumentPlaceholder = (): ReactElement => (
	<svg
		className="kyc-doc-upload__placeholder-svg"
		viewBox="0 0 600 350"
		aria-hidden="true"
		focusable="false"
		preserveAspectRatio="xMidYMid meet"
	>
		<rect x="5" y="5" width="590" height="340" rx="12" fill="#e9ecef" stroke="#cfd4da" />
		<rect x="20" y="20" width="560" height="310" rx="8" fill="#f8f9fa" stroke="#dee2e6" />
		<rect x="40" y="50" width="120" height="120" fill="#dee2e6" stroke="#cfd4da" />
		<circle cx="100" cy="95" r="25" fill="#adb5bd" />
		<rect x="70" y="120" width="60" height="35" rx="10" fill="#adb5bd" />
		<rect x="190" y="60" width="300" height="10" rx="5" fill="#ced4da" />
		<rect x="190" y="85" width="250" height="10" rx="5" fill="#dee2e6" />
		<rect x="190" y="110" width="270" height="10" rx="5" fill="#dee2e6" />
		<rect x="40" y="200" width="500" height="10" rx="5" fill="#ced4da" />
		<rect x="40" y="225" width="520" height="10" rx="5" fill="#dee2e6" />
		<rect x="40" y="250" width="480" height="10" rx="5" fill="#dee2e6" />
	</svg>
);

export const KycDocumentUploadPanel = (props: KycDocumentUploadPanelProps): ReactElement => {
	const {
		disabled,
		previewUrl,
		previewKind = 'image',
		isUploading,
		verificationStatus,
		verificationMessage,
		onSelectFile,
		onTakePhotoClick,
		suppressValidMessage = false,
	} = props;

	const fileInputId = useId();
	const [imageLoadFailed, setImageLoadFailed] = useState(false);
	const actionsDisabled = disabled || isUploading || verificationStatus === 'checking';
	const statusMessageLines = useMemo(
		(): string[] =>
			verificationMessage
				.split('\n')
				.map((line) => line.trim())
				.filter(Boolean),
		[verificationMessage]
	);

	useEffect(() => {
		// New URL selected (or cleared) → allow image to attempt loading again.
		setImageLoadFailed(false);
	}, [previewUrl, previewKind]);

	return (
		<div className={`kyc-doc-upload ${disabled ? 'kyc-doc-upload--disabled' : ''}`}>
			<div className="kyc-doc-upload__preview">
				{previewUrl && !imageLoadFailed ? (
					previewKind === 'pdf' ? (
						<div className="kyc-doc-upload__preview-pdf" aria-label="PDF document uploaded">
							<div className="kyc-doc-upload__preview-pdf-icon" aria-hidden>
								PDF
							</div>
						</div>
					) : (
						<img
							src={previewUrl}
							alt=""
							className="kyc-doc-upload__preview-img"
							role="presentation"
							onError={() => {
								setImageLoadFailed(true);
							}}
						/>
					)
				) : (
					<div className="kyc-doc-upload__preview-empty" aria-hidden>
						<KycDocumentPlaceholder />
					</div>
				)}
			</div>

			<div className="kyc-doc-upload__toolbar">
				<label
					htmlFor={fileInputId}
					className={`kyc-doc-upload__btn kyc-doc-upload__btn--outline ${actionsDisabled ? 'kyc-doc-upload__btn--disabled' : ''}`}
					aria-disabled={actionsDisabled}
				>
					<PersonPlusIcon />
					<span>Upload</span>
				</label>
				<button
					type="button"
					className="kyc-doc-upload__btn kyc-doc-upload__btn--primary"
					disabled={actionsDisabled}
					onClick={onTakePhotoClick}
				>
					<span className="kyc-doc-upload__camera-icon" aria-hidden>
						<svg viewBox="0 0 24 24" width="18" height="18" focusable="false">
							<path
								fill="currentColor"
								d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
							/>
						</svg>
					</span>
					<span>Take a Photo</span>
				</button>
			</div>

			<input
				type="file"
				id={fileInputId}
				className="kyc-doc-upload__hidden-input"
				accept="image/png,image/jpeg,application/pdf,.jpg,.jpeg,.png,.pdf"
				disabled={actionsDisabled}
				onChange={(e) => {
					const file = e.target.files?.[0];
					e.target.value = '';
					if (file) {
						onSelectFile(file);
					}
				}}
			/>

			<div
				className={`kyc-doc-upload__status kyc-doc-upload__status--${verificationStatus}`}
				role="status"
				aria-live="polite"
			>
				{verificationStatus === 'idle' && !isUploading ? <span>Upload or capture a document to verify.</span> : null}
				{(verificationStatus === 'rejected' || verificationStatus === 'max_attempts') && !isUploading ? (
					<div className="kyc-doc-upload__status-messages">
						{(statusMessageLines.length
							? statusMessageLines
							: ['Your document KYC was rejected. Please try again.']
						).map((line, index) => (
							<p key={`${index}-${line}`} className="kyc-doc-upload__status-bad">
								{line}
							</p>
						))}
					</div>
				) : null}
				{isUploading ? <span>Uploading document…</span> : null}
				{verificationStatus === 'checking' && !isUploading ? <span>Verifying document…</span> : null}
				{verificationStatus === 'valid' && !suppressValidMessage ? (
					<span className="kyc-doc-upload__status-ok">{verificationMessage || 'Document verified as valid.'}</span>
				) : null}
				{verificationStatus === 'invalid' ? (
					<span className="kyc-doc-upload__status-bad">{verificationMessage || 'Document not accepted.'}</span>
				) : null}
			</div>
		</div>
	);
};
